import { computed } from 'vue';
import { useError, useHead, useRoute, useRuntimeConfig } from '#imports';
import { useMainStore } from '../stores/mainStore';
import { buildCanonicalUrl, customCodeManagesCanonical, isNoIndexDomain } from '../helpers/canonical';

const EXCLUDED_ROUTE_PATTERN = /^(alias-)?(page-preview|preview|intranet)(-|$)/;

/**
 * Emits a `<link rel="canonical">` for the current route so search engines
 * consolidate the duplicate URLs every page resolves from (any org domain in
 * the family, with or without the `/o/{alias}` prefix, trailing slash or not).
 */
export const useCanonicalTag = () => {
  const mainStore = useMainStore();
  const route = useRoute();
  const error = useError();
  const runtimeConfig = useRuntimeConfig();

  const canonicalLink = computed(() => {
    if (runtimeConfig.public.IS_INTRANET || error.value) return [];
    if (mainStore.isThemePreview || EXCLUDED_ROUTE_PATTERN.test(String(route.name || ''))) return [];
    // Hosts that layouts/default.vue marks noindex must not point canonicals
    // at the production domain — contradictory signals for crawlers.
    if (isNoIndexDomain(mainStore.domain)) return [];
    // Sites whose custom embed code already manages canonicals keep their own;
    // a second platform tag would give crawlers two conflicting canonicals.
    if (customCodeManagesCanonical(mainStore.globals?.settings?.embedCodes?.customCodes)) return [];

    // The alias comes from the route rather than the store so canonicals stay
    // correct when in-content `/o/{alias}` links navigate across orgs client-side.
    const href = buildCanonicalUrl({
      path: route.path,
      domain: mainStore.domain,
      alias: route.params.alias || null,
      organizations: mainStore.organizations,
      query: route.query,
    });
    return href ? [{ rel: 'canonical', href }] : [];
  });

  useHead({ link: canonicalLink });
};
